import { call, put, takeLatest } from 'redux-saga/effects';
import { YieldOf } from 'types/utils';
import { Constants } from 'core';

import { createUser, createdUser, updateUser, updatedUser, deletedUser, deleteUser } from './actions';
import { logout } from '../api-auth/actions';
import getAPI from '../../api/iv-back';
import { sagaWithCallback } from '../utils';

type createActionPayload = ReturnType<typeof createUser>;
type updateUserActionPayload = ReturnType<typeof updateUser>;
type DeleteActionPayload = ReturnType<typeof deleteUser>;

function* createUsersSaga({ payload }: createActionPayload) {
  const { createUserApi } = getAPI();
  const response: YieldOf<typeof createUserApi> = yield call(createUserApi, payload);
  const { ok, problem, data, status } = response;
  if (status === 401 || status === 403) {
    yield put(logout());
  }
  if (ok) {
    yield put(createdUser(undefined));
    return data;
  } else {
    const error =
      status === Constants.ALREADY_EXISTED_STATUS ? Constants.ALREADY_EXISTED_PROBLEM_CODE : problem || 'unknown';
    yield put(createdUser(error));
    throw new Error(data?.errorCode || `create user failed: ${error}`);
  }
}

function* updateUsersSaga({ payload }: updateUserActionPayload) {
  const { modifyUserApi } = getAPI();
  const response: YieldOf<typeof modifyUserApi> = yield call(modifyUserApi, payload);
  const { ok, problem, data, status } = response;
  if (status === 401 || status === 403) {
    yield put(logout());
  }
  if (ok) {
    yield put(updatedUser(undefined, data));
    const isConnectedUser = data?.isCurrentUser;
    if (isConnectedUser) {
      yield put(logout());
    }
    return data;
  } else {
    const error =
      status === Constants.ALREADY_EXISTED_STATUS ? Constants.ALREADY_EXISTED_PROBLEM_CODE : problem || 'unknown';
    yield put(updatedUser(error));
    throw new Error(data?.errorCode || `create user failed: ${error}`);
  }
}

function* deleteUsersSaga({ payload }: DeleteActionPayload) {
  const { deleteUserApi } = getAPI();
  const response: YieldOf<typeof deleteUserApi> = yield call(deleteUserApi, payload.id);
  const { ok, problem, data, status } = response;

  if (status === 401 || status === 403) {
    yield put(logout());
  }
  if (ok) {
    yield put(deletedUser(undefined));
    return data;
  } else {
    const error = problem || 'unknown';
    yield put(deletedUser(error));
    throw new Error(`delete user failed: ${error}`);
  }
}

export default function* createUserSaga() {
  yield takeLatest(createUser.type, sagaWithCallback(createUsersSaga));
  yield takeLatest(updateUser.type, sagaWithCallback(updateUsersSaga));
  yield takeLatest(deleteUser.type, sagaWithCallback(deleteUsersSaga));
}
